// Satisfaction capture: a card above the composer rating the whole thread, and hover thumbs rating one
// turn. Labelling conversations by the user's own satisfaction is what lets the bad use cases be found
// later — the per-turn label is the useful one, since it names which turn went wrong.
//
// Rendered in flow rather than as an overlay: the pane is ~320px wide and an absolutely positioned panel
// above the composer would cover the textarea the user is about to reach for. Every other transient notice
// here (the converge note) is in flow for the same reason.
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FEEDBACK_NEGATIVE_TAGS, FEEDBACK_POSITIVE_TAGS, type FeedbackRating, type FeedbackTag, type TurnRating } from '../../types/agent';

/** Chips offered for a rating. One vocabulary per polarity; the server accepts either for either. */
export function tagsFor(rating: FeedbackRating): readonly FeedbackTag[] {
  return rating === 'down' ? [...FEEDBACK_NEGATIVE_TAGS, 'other'] : [...FEEDBACK_POSITIVE_TAGS, 'other'];
}

/** Client mirror of the server rule, so the two never disagree about what was sent. */
export function normaliseTags(tags: readonly string[], max = 6): FeedbackTag[] {
  const out: FeedbackTag[] = [];
  for (const t of tags) {
    const slug = t.trim().toLowerCase() as FeedbackTag;
    if (!out.includes(slug)) out.push(slug);
  }
  return out.slice(0, max);
}

/** Roughly how tall the comment form is; used only to decide whether it fits above the thumbs. */
const FORM_HEIGHT_ESTIMATE = 190;

const CHOSEN = 'border-accent bg-accent-bg text-accent-dark';
const IDLE = 'border-line-btn text-muted hover:bg-hover hover:text-ink';

/** Thumb glyphs in the house style (Lucide geometry, the 24×24 stroke set of Header / Composer).
 *  The blade fills faintly once chosen, so the selected state reads at a glance without a heavy chip. */
function ThumbIcon({ rating, filled, size }: { rating: FeedbackRating; filled: boolean; size: number }) {
  const fill = filled ? 'currentColor' : 'none';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {rating === 'up' ? (
        <>
          <path d="M7 22V11" />
          <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" fill={fill} fillOpacity={filled ? 0.16 : 0} />
        </>
      ) : (
        <>
          <path d="M17 2v11" />
          <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" fill={fill} fillOpacity={filled ? 0.16 : 0} />
        </>
      )}
    </svg>
  );
}

/** `ghost` for the hover thumbs under a turn (no chrome until you reach for them); `outlined` for the
 *  thread card, where the thumbs are the call to action and have to look pressable. */
type ThumbVariant = 'ghost' | 'outlined';

function Thumbs({ value, onPick, variant = 'outlined', disabled }: { value: FeedbackRating | null; onPick: (r: FeedbackRating) => void; variant?: ThumbVariant; disabled?: boolean }) {
  const { t } = useTranslation();
  const ghost = variant === 'ghost';
  return (
    <div className={`flex items-center ${ghost ? 'gap-0.5' : 'gap-1.5'}`}>
      {(['up', 'down'] as const).map((r) => {
        const chosen = value === r;
        const box = ghost ? 'h-[22px] w-[22px] rounded-md' : 'h-[26px] w-[26px] rounded-md border';
        const tone = ghost
          ? chosen ? 'text-accent-dark' : 'text-fainter hover:bg-hover hover:text-ink'
          : chosen ? CHOSEN : IDLE;
        return (
          <button
            key={r}
            type="button"
            disabled={disabled}
            onClick={() => onPick(r)}
            title={t(`feedback.${r}`)}
            aria-label={t(`feedback.${r}`)}
            aria-pressed={chosen}
            className={`grid shrink-0 place-items-center transition-colors disabled:opacity-50 ${box} ${tone}`}
          >
            {/* The down thumb hangs a hair low so the two blades look optically aligned. */}
            <span className={r === 'down' ? 'translate-y-px' : ''}>
              <ThumbIcon rating={r} filled={chosen} size={ghost ? 13 : 14} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TagChips({ rating, selected, onToggle }: { rating: FeedbackRating; selected: FeedbackTag[]; onToggle: (tag: FeedbackTag) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5">
      {tagsFor(rating).map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={() => onToggle(tag)}
          aria-pressed={selected.includes(tag)}
          className={`rounded-full border px-2 py-[3px] text-[11px] transition-colors ${selected.includes(tag) ? CHOSEN : IDLE}`}
        >
          {t(`feedback.tag.${tag}`)}
        </button>
      ))}
    </div>
  );
}

type Phase = 'ask' | 'detail' | 'sending' | 'thanks' | 'failed';

/** The thread-level card. Step 1 (a thumb) is already a complete answer; the chips and the comment are
 *  optional detail, which is why Send stays enabled with nothing selected. */
export function FeedbackPrompt({ onSubmit, onSkip, onDone }: {
  onSubmit: (rating: FeedbackRating, tags: FeedbackTag[], comment: string) => Promise<void>;
  onSkip: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('ask');
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [tags, setTags] = useState<FeedbackTag[]>([]);
  const [comment, setComment] = useState('');

  const pick = (r: FeedbackRating) => {
    setRating(r);
    setTags([]);
    setPhase('detail');
  };

  /** Back to the question with the thumbs offered again, so a mis-click can be corrected. The chips go
   *  (they are per-polarity) but anything typed stays — that is the part worth not losing. Nothing has
   *  been sent at this point (the ribbon only posts on Send), so there is nothing to retract. */
  const back = () => {
    setRating(null);
    setTags([]);
    setPhase('ask');
  };

  const send = async () => {
    if (!rating) return;
    setPhase('sending');
    try {
      await onSubmit(rating, normaliseTags(tags), comment);
      setPhase('thanks');
      window.setTimeout(onDone, 1400);
    } catch {
      setPhase('failed');
      window.setTimeout(onDone, 3000);
    }
  };

  if (phase === 'thanks' || phase === 'failed') {
    return (
      <div className="mx-3 mb-2 shrink-0 animate-pc-in rounded-lg border border-line bg-white p-2.5 shadow-[0_6px_20px_rgba(0,0,0,.08)]">
        <p className={`text-[12px] ${phase === 'thanks' ? 'text-muted' : 'text-danger'}`}>
          {t(phase === 'thanks' ? 'feedback.thanks' : 'feedback.failed')}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-3 mb-2 flex shrink-0 animate-pc-in flex-col gap-2 rounded-lg border border-line bg-white p-2.5 shadow-[0_6px_20px_rgba(0,0,0,.08)]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] leading-snug text-ink">
          {phase === 'ask' ? t('feedback.question') : t(rating === 'down' ? 'feedback.whatWrong' : 'feedback.whatWorked')}
        </p>
        <div className="flex items-center gap-2">
          {phase === 'ask' && <Thumbs value={rating} onPick={pick} />}
          <button
            type="button"
            onClick={onSkip}
            title={t('feedback.dismiss')}
            aria-label={t('feedback.dismiss')}
            className="text-[13px] leading-none text-muted transition-colors hover:text-ink"
          >
            ×
          </button>
        </div>
      </div>

      {phase !== 'ask' && rating && (
        <>
          <TagChips rating={rating} selected={tags} onToggle={(tag) => setTags((cur) => (cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag]))} />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder={t('feedback.commentPlaceholder')}
            className="w-full resize-none rounded-md border border-line bg-white p-2 text-[12px] leading-snug text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden"
          />
          {/* Back re-offers the thumbs (the only way to correct a mis-click, since they are hidden here);
              `×` dismisses the ribbon and is the one gesture that counts as a dismissal. */}
          <div className="flex items-center justify-end gap-3 text-[11.5px]">
            <button
              type="button"
              onClick={back}
              disabled={phase === 'sending'}
              className="text-muted transition-colors hover:text-ink disabled:text-fainter"
            >
              {t('feedback.back')}
            </button>
            <button
              type="button"
              onClick={send}
              disabled={phase === 'sending'}
              className="font-medium text-accent transition-colors hover:text-accent-dark disabled:text-fainter"
            >
              {t('feedback.send')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Speech bubble — "add a comment to this rating" (Lucide message-square, house stroke set). It fills faintly once a
 *  comment or labels were sent, like a lit thumb, so what was said can be found again. */
function CommentIcon({ size = 13, filled = false }: { size?: number; filled?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill={filled ? 'currentColor' : 'none'} fillOpacity={filled ? 0.16 : 0} />
    </svg>
  );
}

/** Whether a rating carries more than its thumb: labels or a comment, which taking the rating back would delete. */
export function hasDetail(saved: TurnRating | null): boolean {
  return !!saved && (saved.tags.length > 0 || !!saved.comment?.trim());
}

/** What a click on a thumb does, given what was last sent for the turn. The lit thumb takes a bare rating back,
 *  but reopens a commented one: deleting the comment with the rating is how comments used to get lost. The other
 *  thumb re-rates and keeps the comment (labels belong to a polarity, the comment does not). */
export function thumbAction(saved: TurnRating | null, picked: FeedbackRating): 'retract' | 'reopen' | 'rate' {
  if (saved?.rating !== picked) return 'rate';
  return hasDetail(saved) ? 'reopen' : 'retract';
}

/** Hover controls under one assistant turn: the thumbs, and — once one is lit — a comment button that
 *  opens the chips-and-text form.
 *
 *  The thumb alone is the signal and posts on click; the form is deliberately NOT opened for you, because
 *  a panel appearing over the conversation every time someone rates a turn is the interruption this whole
 *  surface is supposed to avoid. Detail is opt-in, and merges onto the same row (the endpoint is an
 *  upsert precisely so the thumb can land first and the comment later).
 *
 *  What was sent lives on the turn's messages (`saved`, from the session, read back with the thread), not
 *  here: the form always reopens on it, so a comment is edited rather than retyped, and a reloaded pane never
 *  posts a bare thumb over a stored comment. Only the draft being edited is local. */
export function MessageFeedback({ turnIndex, saved, onRate, onRetract }: {
  turnIndex: number;
  saved: TurnRating | null;
  onRate: (turnIndex: number, rating: FeedbackRating, tags: FeedbackTag[], comment: string) => void;
  onRetract?: (turnIndex: number) => void;
}) {
  const { t } = useTranslation();
  const rating = saved?.rating ?? null;
  const detailed = hasDetail(saved);
  const [tags, setTags] = useState<FeedbackTag[]>([]);
  const [comment, setComment] = useState('');
  const [open, setOpen] = useState(false);
  const [thanks, setThanks] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  // Which way the form opens. It prefers upwards, but a turn near the top of the pane has no room there
  // and the panel was being cut off by the scroll container — so measure and drop downwards instead.
  const [dropDown, setDropDown] = useState(false);

  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const rect = anchor.current.getBoundingClientRect();
    setDropDown(rect.top < FORM_HEIGHT_ESTIMATE);
  }, [open]);

  /** The form, filled with what was sent: Cancel throws the edits away, so reopening shows what is stored. */
  const reopen = () => {
    setTags(saved ? [...saved.tags] : []);
    setComment(saved?.comment ?? '');
    setOpen(true);
  };

  /** Un-light the thumb AND delete the row. The thumb posts on click, so clearing it here without
   *  retracting would leave a stored rating the UI no longer shows — a stale 👎 is worse than no row. */
  const retract = () => {
    setTags([]);
    setComment('');
    setOpen(false);
    setThanks(false);
    onRetract?.(turnIndex);
  };

  const pick = (r: FeedbackRating) => {
    const action = thumbAction(saved, r);
    if (action === 'retract') return retract();
    if (action === 'reopen') return reopen();
    const kept = saved?.comment ?? '';
    setTags([]);
    setThanks(false);
    onRate(turnIndex, r, [], kept);
    // A comment survives the switch but lost its labels: open it so the new thumb gets labels of its own.
    if (kept && !open) {
      setComment(kept);
      setOpen(true);
    }
  };

  const sendDetail = () => {
    if (!rating || !tags.length) return;
    onRate(turnIndex, rating, normaliseTags(tags), comment);
    setOpen(false);
    setThanks(true);
    window.setTimeout(() => setThanks(false), 1600);
  };

  // Pinned once rated: the lit thumb is the record of what you said about this turn, so it stays put
  // instead of fading out with the rest of the hover controls.
  const pinned = rating !== null || open;
  const commentLabel = t(detailed ? 'feedback.editComment' : 'feedback.comment');
  return (
    <div className={`transition-opacity duration-150 ease-out motion-reduce:transition-none ${pinned ? 'opacity-100' : 'opacity-0 focus-within:opacity-100 group-hover:opacity-100'}`}>
      <div ref={anchor} className="relative flex items-center gap-1.5">
        <Thumbs value={rating} onPick={pick} variant="ghost" />
        {rating && (
          <button
            type="button"
            onClick={() => (open ? setOpen(false) : reopen())}
            title={commentLabel}
            aria-label={commentLabel}
            aria-expanded={open}
            className={`grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md transition-colors ${open ? 'bg-hover text-ink' : detailed ? 'text-accent-dark hover:bg-hover' : 'text-fainter hover:bg-hover hover:text-ink'}`}
          >
            <CommentIcon filled={detailed} />
          </button>
        )}
        {thanks && <span className="text-[11px] text-fainter">{t('feedback.thanks')}</span>}
        {open && rating && (
          <div className={`absolute left-0 z-20 flex w-[232px] max-w-[calc(100vw-2rem)] animate-pc-in-fast flex-col gap-1.5 rounded-md border border-line bg-white p-2 shadow-[0_6px_20px_rgba(0,0,0,.08)] ${dropDown ? 'top-[calc(100%+6px)]' : 'bottom-[calc(100%+6px)]'}`}>
            <TagChips rating={rating} selected={tags} onToggle={(tag) => setTags((cur) => (cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag]))} />
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              autoFocus
              placeholder={t('feedback.commentPlaceholder')}
              className="w-full resize-none rounded-md border border-line bg-white p-1.5 text-[12px] leading-snug text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden"
            />
            {/* Send waits for a label: the labels are what the bad turns are found by, and a comment sent without
                one looked like a click that did nothing. Said in words once the user has typed. */}
            {!tags.length && comment.trim() && <p className="text-[11px] leading-snug text-fainter">{t('feedback.pickLabel')}</p>}
            <div className="flex items-center gap-3 text-[11.5px]">
              {/* With a comment stored, the lit thumb reopens this form instead of taking the rating back: this is
                  where the rating, comment and labels together are withdrawn. */}
              {detailed && (
                <button type="button" onClick={retract} className="text-muted transition-colors hover:text-danger">
                  {t('feedback.remove')}
                </button>
              )}
              {/* Closes the form and keeps the rating — the thumb is a separate decision. */}
              <button type="button" onClick={() => setOpen(false)} className="ml-auto text-muted transition-colors hover:text-ink">
                {t('feedback.cancel')}
              </button>
              <button
                type="button"
                onClick={sendDetail}
                disabled={!tags.length}
                className="font-medium text-accent transition-colors hover:text-accent-dark disabled:cursor-not-allowed disabled:text-fainter"
              >
                {t('feedback.send')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
